import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form } from "reactstrap";
import { COLOR_PRIMARY } from "../../../../constants/theme";
import { InputWembii } from "../../InputWembii";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../../cores/me";
import { useAutomationCore } from "../../../../cores/automations";
import { AutomationTriggerOptions } from "../../../../cores/automations/config";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { useGlobalCore } from "../../../../cores/globals";
import { AUTOMATION_CREATE_FORM, AUTOMATION_EDIT_FORM } from "../../../../constants/modal.constant";
import { Contact } from "../../../../cores/contacts/interfaces/api/get-contacts";
import { AutomationTrigger } from "../../../../cores/automations/enum/AutomationTrigger.enum";

interface Props {
  contacts?: Contact[];
  automation?: AutomationTrigger;
}

export default function CreateAutomationForm(props: Props) {
  const { contacts = [], automation } = props;
  const intl = useIntl();
  const { company } = useMeCore();
  const { postAutomation, getAutomation } = useAutomationCore();
  const { turnOffModal, turnOnModal } = useGlobalCore();
  
  const [name, setName] = useState("");
  const [trigger, setTrigger] = useState("");

  useEffect(() => {
    if (automation) {
      setTrigger(automation);
    }
  }, [automation]);

  const onFormSubmit = async (e: any) => {
    e.preventDefault();

    const contactsIds = contacts.map((contact) => contact.id);
    const id = await postAutomation(
      company?.id!,
      { name, trigger, contacts: contactsIds },
      () => {
        turnOffModal(AUTOMATION_CREATE_FORM);
      }
    );

    if (id) {
      turnOnModal(AUTOMATION_EDIT_FORM, true);
      getAutomation(company?.id!, id);
    }
  }

  const disabled = useCallback(
    () => !name || !trigger,
    [name, trigger]
  );

  const options = useMemo(() => {
    if (automation) {
      return AutomationTriggerOptions.filter((option) => option.value === automation);
    } else {
      return AutomationTriggerOptions;
    }
  }, [automation]);

  return (
    <div className="automation-modal automation-create">
      <div className="automation-modal__hero">
        <span className="automation-modal__eyebrow">Nuevo flujo</span>
        <h2>{intl.formatMessage({ id: "app.automations.automation" })}</h2>
        <p>
          Elige el evento que dispara la automatización. Después podrás
          agregar condiciones y acciones sin perder esta configuración.
        </p>
      </div>

      <Form className="automation-form" onSubmit={onFormSubmit}>
        <div className="automation-form__section">
          <span className="automation-step">1</span>
          <div>
            <h3>Nombra la automatización</h3>
            <p>Usa un nombre fácil de reconocer para tu equipo.</p>
          </div>
        </div>

        <InputWembii
          label={intl.formatMessage({ id: "app.automations.automation.name" })}
          type="text"
          name="name"
          id="name"
          placeholder="Ej: Bienvenida a nuevos leads"
          value={name}
          required
          onChange={setName}
        />

        <div className="automation-form__section">
          <span className="automation-step">2</span>
          <div>
            <h3>{intl.formatMessage({ id: "app.automations.automation.trigger" })}</h3>
            <p>Define cuándo Wembii debe iniciar este flujo.</p>
          </div>
        </div>

        <div className="automation-trigger-grid">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setTrigger(option.value)}
              className={cn("automation-trigger-card", {
                "is-selected": trigger === option.value,
              })}
            >
              <span>
                <FontAwesomeIcon
                  icon={["fas", option.icon as IconName]}
                  color={trigger === option.value ? COLOR_PRIMARY : undefined}
                />
              </span>
              <strong>{intl.formatMessage({ id: option.label })}</strong>
              <small>
                {option.value === AutomationTrigger.NEW_CONTACT
                  ? "Cuando ingresa un lead nuevo al CRM."
                  : option.value === AutomationTrigger.CONTACT
                  ? "Para una lista específica de contactos."
                  : "Cuando se asigna un responsable."}
              </small>
            </button>
          ))}
        </div>

        <Button color="primary" block disabled={disabled()} className="automation-submit-btn">
          {intl.formatMessage({ id: "app.automations.automation.create" })}
        </Button>
      </Form>
    </div>
  );
}
